import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
import {
  AutoPolicy,
  GracePeriodDays,
  Policy,
  RiskModel,
  RiskSelectionContext
} from '@amfam/policy/models';
import { VechiclePolicyChangeEntryPoint, VehicleFacade } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { userQuery, UserState } from '@amfam/shared/user';
import { Agent, agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
// Store
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';

import * as fromRoot from '../../../core/store';
import * as replaceVehicleActions from '../../../core/store/replace-vehicle/replace-vehicle.actions';
import * as coverageChangeSelector from '../../+state';

@Component({
  selector: 'app-policy-summary',
  templateUrl: './policy-summary.component.html',
  styleUrls: ['./policy-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PolicySummaryComponent implements OnInit, OnDestroy {
  @Input() policies: Policy[] = new Array<Policy>();
  @Input() advancedAutoPolicies: RiskModel[] = new Array<RiskModel>();
  @Input() hasCompAndCollisonCoverage: boolean;
  @Input() summary: string;
  @Input() url: string;
  @Input() type: string;
  @Input() isImpersonating: boolean;
  @Input() isSpecialtyLine: boolean = false;
  @Input() isPolicyCancelled: boolean = false;

  addVehicleEnabled$ = this.store.select(fromRoot.getAddVehicleEnabled);
  partnerAddVehicleEnabled$ = this.store.select(fromRoot.partnerAddVehicleEnabled);
  displayPolicyGracePeriod = false;
  activeAgents$: Observable<Agent[]>;
  getAdvanceAutoPolicies$: Observable<AutoPolicy[]>;
  userState$: Observable<UserState>;
  comprehensiveCoverageInputValues$: Observable<string[]>;
  collisionCoverageInputValues$: Observable<string[]>;
  showChangeMyCoverageButton$: Observable<boolean>;
  activeAutoPolicies$ = this.store.select(fromRoot.getActiveScheduledAutoPolicies);
  gracePeriodDays$: Observable<string>;
  changeCoverageEnabled$ = this.store
    .select(BrandSelectors.selectIsPartner)
    .pipe(
      map(isPartner =>
        isPartner
          ? this.featureFlagService.isEnabled('partner_model_coverage')
          : this.featureFlagService.isEnabled('model_coverage')
      )
    );

  // TODO: this will be removed when partners and non-partners
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  private stop$: Subject<void> = new Subject<void>();
  isPartner: boolean = false;
  constructor(
    private featureFlagService: FeatureFlagService,
    private store: Store<fromRoot.RootState>,
    private copyService: CopyService,
    private vehicleFacade: VehicleFacade,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit() {
    this.store.select(BrandSelectors.selectIsPartner).subscribe(ele => {
      this.isPartner = ele;
    });
    this.comprehensiveCoverageInputValues$ = this.store.select(
      coverageChangeSelector.selectComprehensiveCoverageLimits
    );
    this.collisionCoverageInputValues$ = this.store.select(
      coverageChangeSelector.selectCollisionCoverageLimits
    );
    this.activeAgents$ = combineLatest([
      this.store.select(fromRoot.getAllActiveAgents),
      this.store.select(agentQuery.getAreAllAgentsLoaded)
    ]).pipe(
      map(([agents, loaded]) => ({ agents, loaded })),
      filter(data => !!data.loaded),
      map(data => data.agents)
    );

    this.showChangeMyCoverageButton$ = combineLatest([
      this.comprehensiveCoverageInputValues$,
      this.collisionCoverageInputValues$,
      this.store.select(userQuery.selectUserStateCode)
    ]).pipe(
      map(([comprehensiveValues, collisionValues, userStateCode]) => ({
        comprehensiveValues,
        collisionValues,
        userStateCode
      })),
      map(data => {
        let unsupportedStates = this.copyService.getCopy(
          'policy.changeCoverage.updateCoverageUnsupportedStates'
        );
        if (!this.isPartner) {
          unsupportedStates = this.copyService.getCopy(
            'policy.changeCoverage.updateCoverageAFIUnsupportedStates'
          );
        }
        return (
          data.comprehensiveValues.length > 0 &&
          data.collisionValues.length > 0 &&
          this.advancedAutoPolicies.length > 0 &&
          this.hasCompAndCollisonCoverage &&
          !this.isImpersonating &&
          !unsupportedStates.includes(data.userStateCode.toLowerCase())
        );
      })
    );

    this.getAdvanceAutoPolicies$ = this.store.select(PolicySelectors.selectAdvanceAutoPolicies);
    this.userState$ = this.store.select(userQuery.selectUserState);

    /*
      This can display for both classic and advanced users, so the logic is different here
      than in the add, replace vehicle flows as they only occur for advanced policies.
    */
    this.gracePeriodDays$ = combineLatest([
      this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
      this.store.select(userQuery.selectUserStateCode)
    ]).pipe(
      takeUntil(this.stop$),
      map((advancedAutoPolicies, userState) => {
        const advancedAutoPoliciesLength = _get(advancedAutoPolicies, 'length', 0);
        const isExtendedGracePeriodState = !!this.copyService
          .getCopy('policy.extendedGracePeriodStates')
          .find(state => state === userState);
        return advancedAutoPoliciesLength > 0 || !isExtendedGracePeriodState
          ? GracePeriodDays.REGULAR // 14
          : GracePeriodDays.EXTENDED; // 30
      })
    );
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  replaceVehicle() {
    this.vehicleFacade.setVehiclePolicyChangeEntryPoint(VechiclePolicyChangeEntryPoint.POLICY);
    this.store.dispatch(
      new replaceVehicleActions.SetVehicleRiskSelectionContext(RiskSelectionContext.REPLACE_VEHICLE)
    );

    const vehicleId =
      this.advancedAutoPolicies.length > 1 ? '' : this.advancedAutoPolicies[0]?.vehicleId;

    // if multiple autoPolicies, route to risk-selection page
    if (this.advancedAutoPolicies.length > 1) {
      return this.store.dispatch(
        fromRouterActions.Go({
          path: ['/policies/risk-selection']
        })
      );
    }

    combineLatest([
      this.store.select(fromRoot.hasAnyPolicyCompAndCollisonCoverageAndCarLightTruck),
      this.store.select(fromRoot.isValidVehicleToBeReplaced(vehicleId)),
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(PolicySelectors.selectHasClassicPolicies),
      this.store.select(PolicySelectors.selectHasAdvancePolicies),
      this.store.select(PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage),
      this.store.select(fromRoot.hasAnyPolicyLiabilityCoverage)
    ])
      .pipe(
        map(
          ([
            isEligibleForAddVehicleFlow,
            isValidVehicleToBeReplaced,
            isPartner,
            hasClassicPolicy,
            advancePolicy,
            hasOnlyPolicyComprehensiveCoverage,
            hasAnyPolicyLiabilityCoverage
          ]) => ({
            isEligibleForAddVehicleFlow,
            isValidVehicleToBeReplaced,
            isPartner,
            hasClassicPolicy,
            advancePolicy,
            hasOnlyPolicyComprehensiveCoverage,
            hasAnyPolicyLiabilityCoverage
          })
        ),
        take(1)
      )
      .subscribe(
        ({
          isEligibleForAddVehicleFlow,
          isValidVehicleToBeReplaced,
          isPartner,
          hasClassicPolicy,
          advancePolicy,
          hasOnlyPolicyComprehensiveCoverage,
          hasAnyPolicyLiabilityCoverage
        }) => {
          let route = [];
          const addVehicleSkipHardstop = this.featureFlagService.isEnabled(
            'add_vehicle_skip_hardstop'
          );
          const isShowhardstopRiskSelectionPage =
            this.featureFlagService.isEnabled('add_vehicle') &&
            addVehicleSkipHardstop &&
            ((hasClassicPolicy && !advancePolicy && !isPartner) ||
              (advancePolicy && !isPartner && hasOnlyPolicyComprehensiveCoverage) ||
              hasAnyPolicyLiabilityCoverage);
          if (
            (isValidVehicleToBeReplaced ||
              (this.featureFlagService.isEnabled('add_vehicle') && !!isEligibleForAddVehicleFlow) ||
              isShowhardstopRiskSelectionPage) &&
            !this.isSpecialtyLine &&
            this.type === 'auto'
          ) {
            route = ['/policies/risk-selection'];
          } else {
            route = ['/policies/risk-selection', 'grace-period'];
          }

          this.store.dispatch(
            fromRouterActions.Go({
              path: route
            })
          );
        }
      );
  }

  coverageChange() {
    if (this.advancedAutoPolicies.length === 1) {
      this.store
        // TODO: this will be removed when partners and non-partners
        .select(BrandSelectors.selectIsPartner)
        .pipe(withLatestFrom(this.store.select(PolicySelectors.selectAdvanceAutoPolicies)), first())
        .subscribe(([isPartner, policies]) => {
          const policy = policies.find(
            p => p.policyNumber === this.advancedAutoPolicies[0].policyNumber
          );
          const vehicle = policy.vehicles.find(
            v => v.vehicleId === this.advancedAutoPolicies[0].vehicleId
          );
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/change-coverage/' + vehicle.vehicleId + '/get-quote']
            })
          );
        });
    } else {
      this.store.dispatch(
        fromRouterActions.Go({
          path: ['/policies/coverage-change']
        })
      );
    }
  }

  public routeToAddVehicle() {
    this.vehicleFacade.setVehiclePolicyChangeEntryPoint(VechiclePolicyChangeEntryPoint.POLICY);
    combineLatest([
      this.store.select(fromRoot.hasAnyPolicyCompAndCollisonCoverageAndCarLightTruck),
      // TODO: this will be removed when partners and non-partners
      this.store.select(BrandSelectors.selectIsPartner)
    ])
      .pipe(
        map(([isEligibleForAddVehicleFlow, isPartner]) => ({
          isEligibleForAddVehicleFlow,
          isPartner
        })),
        take(1)
      )
      .subscribe(({ isEligibleForAddVehicleFlow, isPartner }) => {
        let addVehicleRoute;

        if (
          isPartner &&
          this.featureFlagService.isEnabled('add_vehicle') &&
          isEligibleForAddVehicleFlow
        ) {
          addVehicleRoute = ['/policies/add-vehicle', 'expectation'];
        } else if (
          this.featureFlagService.isEnabled('add_vehicle') &&
          isEligibleForAddVehicleFlow
        ) {
          addVehicleRoute = ['/policies/risk-selection'];
        } else if (isPartner) {
          addVehicleRoute = ['/policies/add-vehicle/agent-help'];
        } else {
          addVehicleRoute = ['/policies/risk-selection', 'grace-period'];
        }

        this.store.dispatch(
          fromRouterActions.Go({
            path: addVehicleRoute
          })
        );
      });
  }
}
